import { Configuration } from '../../api-contracts/runtime';
import {
  InspectionApi,
  ReportApi,
  UserMessageApi,
} from '../../api-contracts/apis';
import azureAuthService from '../azureAuthService';
import { createRequestTransport } from './requestTransport';

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(signal.reason);
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        reject(signal.reason);
      },
      { once: true },
    );
  });

const createConfiguration = () =>
  new Configuration({
    basePath: import.meta.env.VITE_API_URL,
    credentials: 'include',
    fetchApi: createRequestTransport({
      // Looked up per call, so a fetch replaced after start-up is the one used.
      fetch: (input, init) => fetch(input, init),
      session: {
        getToken: () => azureAuthService.getAccessToken(),
        refresh: () => azureAuthService.renewTokenSilently(),
      },
      now: () => performance.now(),
      sleep,
    }),
  });

/** Builds each generated API client once, all sharing one request transport. */
export class ApiFactory {
  private static configuration: Configuration;
  private static inspectionApi: InspectionApi;
  private static reportApi: ReportApi;
  private static userMessageApi: UserMessageApi;

  private static getConfiguration(): Configuration {
    this.configuration ??= createConfiguration();
    return this.configuration;
  }

  static getInspectionApi(): InspectionApi {
    this.inspectionApi ??= new InspectionApi(this.getConfiguration());
    return this.inspectionApi;
  }

  static getReportApi(): ReportApi {
    this.reportApi ??= new ReportApi(this.getConfiguration());
    return this.reportApi;
  }

  static getUserMessageApi(): UserMessageApi {
    this.userMessageApi ??= new UserMessageApi(this.getConfiguration());
    return this.userMessageApi;
  }
}
